import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, Studio, UserRole, ManagementCompanyPermissions, PartnerSummary } from '@/types'
import { supabase } from '@/lib/supabase'
import type { MetricKey, ScopeKey, UnitKey } from '@/lib/ratings/unifiedRatings'
import type { StudioAccessSyncStatus } from '@/lib/studioAccessState'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (user: User) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  signInWithMock: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      login: (user) => {
        set({ user, isAuthenticated: true, isLoading: false })
      },
      logout: () => {
        set({ user: null, isAuthenticated: false, isLoading: false })

        // Очищаем кастомный JWT и auth-артефакты Supabase в клиентском хранилище.
        localStorage.removeItem('auth_token')
        sessionStorage.removeItem('auth_token')
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
            localStorage.removeItem(key)
          }
        })
        Object.keys(sessionStorage).forEach((key) => {
          if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
            sessionStorage.removeItem(key)
          }
        })

        // Сбрасываем Supabase сессию
        supabase.auth.signOut().catch(console.error)
      },
      setLoading: (isLoading) => {
        set({ isLoading })
      },
      signInWithMock: async () => {
        set({ isLoading: true })

        try {
          // Создаем мок-пользователя
          const mockUser: User = {
            id: 'mock-user-id',
            telegram_id: 123456789,
            tg_username: 'test_user',
            first_name: 'Тест',
            last_name: 'Пользователь',
            phone_number: undefined,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }

          // Симулируем задержку
          await new Promise(resolve => setTimeout(resolve, 1000))

          set({ user: mockUser, isAuthenticated: true, isLoading: false })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
)

interface StudioState {
  studios: Studio[]
  partners: PartnerSummary[]
  selectedStudio: Studio | null
  selectedPartnerNumber: number | null
  studiosSynced: boolean
  studiosSyncStatus: StudioAccessSyncStatus
  studiosSyncError: string | null
  setStudios: (studios: Studio[]) => void
  beginStudiosSync: () => void
  failStudiosSync: (message?: string) => void
  resetStudiosAccess: () => void
  setPartners: (partners: PartnerSummary[]) => void
  setSelectedStudio: (studio: Studio | null) => void
  setSelectedPartnerNumber: (partnerNumber: number | null) => void
  getUserRole: (studioId: number) => UserRole | null
  getPartnerStudios: (partnerNumber: number | null) => Studio[]
  isManagementCompany: () => boolean
  getAccessPermissions: () => ManagementCompanyPermissions | null
}

export const useStudioStore = create<StudioState>()(
  persist(
    (set, get) => ({
      studios: [],
      partners: [],
      selectedStudio: null,
      selectedPartnerNumber: null,
      studiosSynced: false,
      studiosSyncStatus: 'idle',
      studiosSyncError: null,
      setStudios: (studios) => {
        set((state) => ({
          studios,
          studiosSynced: true,
          studiosSyncStatus: 'ready',
          studiosSyncError: null,
          selectedStudio: state.selectedStudio
            ? studios.find((studio) => studio.studio_internal_id === state.selectedStudio?.studio_internal_id) ?? null
            : null,
        }))
      },
      beginStudiosSync: () => {
        set({ studiosSyncStatus: 'loading', studiosSyncError: null })
      },
      failStudiosSync: (message = 'Не удалось проверить доступ к студиям') => {
        set({ studiosSyncStatus: 'error', studiosSyncError: message })
      },
      resetStudiosAccess: () => {
        set({
          studios: [],
          partners: [],
          selectedStudio: null,
          selectedPartnerNumber: null,
          studiosSynced: false,
          studiosSyncStatus: 'idle',
          studiosSyncError: null,
        })
      },
      setPartners: (partners) => {
        set({ partners })
      },
      setSelectedStudio: (selectedStudio) => set({ selectedStudio }),
      setSelectedPartnerNumber: (selectedPartnerNumber) =>
        set((state) => (
          state.selectedPartnerNumber === selectedPartnerNumber
            ? state
            : { selectedPartnerNumber }
        )),
      getUserRole: (studioId) => {
        const { studios } = get()
        const studio = studios.find(s => s.studio_internal_id === studioId)
        return studio?.role || null
      },
      getPartnerStudios: (partnerNumber) => {
        const { studios } = get()
        if (partnerNumber === null) {
          return studios
        }

        const normalizePartnerNumber = (value: unknown): number | null => {
          if (value === null || value === undefined || value === '') {
            return null
          }
          const parsed = Number(value)
          return Number.isNaN(parsed) ? null : parsed
        }

        const withPartnerData = studios.filter(
          (studio) => normalizePartnerNumber(studio.partner_number) !== null
        )
        if (withPartnerData.length === 0) {
          // В partner-контуре без данных о partner_number безопаснее не показывать "все студии".
          return []
        }

        return studios.filter(
          (studio) => normalizePartnerNumber(studio.partner_number) === partnerNumber
        )
      },
      isManagementCompany: () => {
        const { studios } = get()
        return studios.some(
          (studio) =>
            studio.access_kind === 'management_company' ||
            studio.role === 'management_company'
        )
      },
      getAccessPermissions: () => {
        const { studios } = get()
        const isManagementCompany = studios.some(
          (studio) =>
            studio.access_kind === 'management_company' ||
            studio.role === 'management_company'
        )

        if (isManagementCompany) {
          return {
            viewAllStudios: true,
            viewAnalytics: true,
            viewMetrics: true,
            viewConfirmationStatus: true,
            exportData: true,
            editMetrics: false,
            confirmRevenue: false,
          }
        }

        return null
      },
    }),
    {
      name: 'studio-storage',
      partialize: (state) => ({
        studios: state.studios,
        partners: state.partners,
        selectedStudio: state.selectedStudio,
        selectedPartnerNumber: state.selectedPartnerNumber
      }),
    }
  )
)

export type RatingsPeriodRangeKey = 'period' | 'yesterday' | `week-${number}`

interface RatingsFiltersState {
  metric: MetricKey
  unit: UnitKey
  scope: ScopeKey
  selectedPeriodId: number | null
  selectedRangeKey: RatingsPeriodRangeKey
  setMetric: (metric: MetricKey) => void
  setUnit: (unit: UnitKey) => void
  setScope: (scope: ScopeKey) => void
  setSelectedPeriodId: (periodId: number) => void
  setSelectedRangeKey: (rangeKey: RatingsPeriodRangeKey) => void
}

// Фильтры partner-рейтинга живут до перезагрузки вкладки и не сбрасываются
// при переходе на другую студию, даже если RatingsPage размонтируется.
export const useRatingsFiltersStore = create<RatingsFiltersState>((set) => ({
  metric: 'revenue',
  unit: 'studio',
  scope: 'network',
  selectedPeriodId: null,
  selectedRangeKey: 'period',
  setMetric: (metric) => set({ metric }),
  setUnit: (unit) => set({ unit }),
  setScope: (scope) => set({ scope }),
  setSelectedPeriodId: (selectedPeriodId) => set({ selectedPeriodId }),
  setSelectedRangeKey: (selectedRangeKey) => set({ selectedRangeKey }),
}))

interface AppState {
  currentMonth: string
  isOnline: boolean
  setCurrentMonth: (month: string) => void
  setOnline: (online: boolean) => void
}

export const useAppStore = create<AppState>((set) => {
  const now = new Date()
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

  return {
    currentMonth,
    isOnline: navigator.onLine,
    setCurrentMonth: (currentMonth) => set({ currentMonth }),
    setOnline: (isOnline) => set({ isOnline }),
  }
})
